import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  PermissionSelectDesignTokens,
} from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

type PermissionSelectTokenSources = {
  paddingLeft?: string;
  surfacePrimary?: string;
  surfaceHover?: string;
  surfacePressed?: string;
  neutralInactive?: string;
  activeColor?: string;
  inactiveColor?: string;
  focusBorderColor?: string;
  focusBoxShadow?: string;
  textPrimary?: string;
  textSecondary?: string;
  textInactive?: string;
  dividerColor?: string;
  spacing4?: string;
  spacing8?: string;
  spacing12?: string;
  spacing16?: string;
  indicatorGap?: string;
  borderRadius?: string;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string | number;
  lineHeight?: string;
  ellipsisOverflow?: string;
  ellipsisTextOverflow?: string;
};

const PILL_TRANSITION = 'background-color 0.2s linear';
const CONTROL_HEIGHT = '40px';
const BORDER_2PX_SOLID_TRANSPARENT = {
  borderWidth: '2px',
  borderStyle: 'solid' as const,
  borderColor: 'transparent',
};
const BORDER_1PX_SOLID = {
  borderWidth: '1px',
  borderStyle: 'solid' as const,
};
const TOGGLE_BUTTON = {
  defaultVisibility: 'hidden' as const,
  hoverVisibility: 'visible' as const,
  openVisibility: 'visible' as const,
};

const buildPermissionSelectTokens = (
  sources: PermissionSelectTokenSources,
): PermissionSelectDesignTokens => {
  const pillBase = {
    width: '24px',
    height: '8px',
    marginRight: sources.spacing4,
    borderRadius: sources.borderRadius,
    transition: PILL_TRANSITION,
  };

  const labelTypography = {
    fontFamily: sources.fontFamily,
    fontSize: sources.fontSize,
    fontWeight: sources.fontWeight,
    lineHeight: sources.lineHeight,
  };

  return {
    control: {
      height: CONTROL_HEIGHT,
      paddingLeft: sources.paddingLeft,
      backgroundColor: sources.surfacePrimary,
      boxShadow: 'none',
      ...BORDER_2PX_SOLID_TRANSPARENT,
      focusBorderWidth: '2px',
      focusBorderStyle: 'solid',
      focusBorderColor: sources.focusBorderColor,
      focusBoxShadow: sources.focusBoxShadow,
      hover: {
        backgroundColor: sources.surfaceHover,
        inactivePillBackgroundColor: sources.neutralInactive,
      },
      active: {
        backgroundColor: sources.surfacePressed,
        inactivePillBackgroundColor: sources.neutralInactive,
        ...BORDER_2PX_SOLID_TRANSPARENT,
      },
      open: {
        backgroundColor: sources.surfacePrimary,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: sources.focusBorderColor,
        boxShadow: sources.focusBoxShadow,
        inactivePillBackgroundColor: sources.neutralInactive,
      },
      disabled: {
        backgroundColor: sources.surfacePrimary,
      },
      toggleButton: TOGGLE_BUTTON,
    },
    indicator: {
      container: {
        overflow: 'hidden',
      },
      row: {
        height: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      pill: {
        empty: {
          ...pillBase,
          backgroundColor: sources.neutralInactive,
        },
        full: {
          ...pillBase,
          backgroundColor: sources.activeColor,
          disabled: {
            backgroundColor: sources.inactiveColor,
          },
        },
        semi: {
          ...pillBase,
          ...BORDER_1PX_SOLID,
          borderColor: sources.activeColor,
          disabled: {
            ...BORDER_1PX_SOLID,
            borderColor: sources.inactiveColor,
          },
        },
        inactive: {
          backgroundColor: sources.neutralInactive,
        },
      },
      label: {
        marginTop: '3px',
        textAlign: 'center' as const,
        color: sources.textPrimary,
        ...labelTypography,
        overflow: sources.ellipsisOverflow,
        textOverflow: sources.ellipsisTextOverflow,
        whiteSpace: 'nowrap' as const,
      },
    },
    option: {
      label: {
        height: '24px',
        userSelect: 'none' as const,
        color: sources.textSecondary,
        ...labelTypography,
        disabled: {
          color: sources.textInactive,
        },
      },
    },
    menu: {
      groupHeader: {
        display: 'flex',
        alignItems: 'center',
        borderTopWidth: '1px',
        borderTopStyle: 'solid' as const,
        borderTopColor: sources.dividerColor,
        ...labelTypography,
      },
      itemPadding: {
        small: {
          top: sources.spacing8,
          right: sources.spacing16,
          bottom: sources.spacing8,
          left: sources.spacing16,
        },
        normal: {
          top: sources.spacing12,
          right: sources.spacing16,
          bottom: sources.spacing12,
          left: sources.spacing16,
        },
        firstExtra: sources.spacing8,
        lastExtra: sources.spacing8,
      },
      indicatorGap: sources.indicatorGap,
    },
  };
};

export const getPermissionSelectTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): PermissionSelectDesignTokens => {
  const bodySmall = theme.typography.bodySmall;
  const ellipsis = theme.typography.ellipsis;

  return buildPermissionSelectTokens({
    paddingLeft: px(theme.spacing.M),
    surfacePrimary: theme.tokens.colors.background.primary.value,
    surfaceHover: theme.tokens.colors.background.neutral.hover.value,
    surfacePressed: theme.tokens.colors.background.neutral.pressed.value,
    neutralInactive: theme.tokens.colors.background.neutral.inactive.value,
    activeColor: theme.tokens.colors.background.active.value,
    inactiveColor: theme.tokens.colors.background.inactive.value,
    focusBorderColor: theme.colors.focusHalo,
    focusBoxShadow: `0 0 0 2px ${theme.colors.focusHalo}`,
    textPrimary: theme.tokens.colors.text.primary.value,
    textSecondary: theme.tokens.colors.text.secondary.value,
    textInactive: theme.tokens.colors.text.inactive.value,
    dividerColor: theme.tokens.colors.border.subtle.value,
    spacing4: px(theme.spacing.XXS),
    spacing8: px(theme.spacing.XS),
    spacing12: px(theme.spacing.S),
    spacing16: px(theme.spacing.M),
    indicatorGap: '6px',
    borderRadius: px(theme.borderRadius.xxLarge),
    fontFamily: bodySmall.fontFamily,
    fontSize: bodySmall.fontSize,
    fontWeight: bodySmall.fontWeight,
    lineHeight: bodySmall.lineHeight,
    ellipsisOverflow: ellipsis.overflow,
    ellipsisTextOverflow: ellipsis.textOverflow,
  });
};

export const getPermissionSelectTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): PermissionSelectDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const menu = tokens?.component?.menu;

  return buildPermissionSelectTokens({
    paddingLeft: generic?.spacing?.[16],
    surfacePrimary: semantic?.['background-color']?.surface?.[100],
    surfaceHover: semantic?.['background-color']?.ghost?.high?.hover,
    surfacePressed: semantic?.['background-color']?.ghost?.high?.pressed,
    neutralInactive: semantic?.['background-color']?.neutral?.high?.default,
    activeColor: semantic?.['background-color']?.accent,
    inactiveColor: semantic?.['status-color']?.read,
    focusBorderColor: semantic?.['border-color']?.accent?.default,
    focusBoxShadow: semantic?.halo?.default?.focus,
    textPrimary: semantic?.['text-color']?.medium?.default,
    textSecondary: semantic?.['text-color']?.light,
    textInactive: semantic?.['text-color']?.placeholder,
    dividerColor: semantic?.['border-color']?.divider?.light,
    spacing4: generic?.spacing?.[4],
    spacing8: generic?.spacing?.[8],
    spacing12: generic?.spacing?.[12],
    spacing16: generic?.spacing?.[16],
    indicatorGap: '6px',
    borderRadius: generic?.['border-radius']?.[4],
    fontFamily,
    fontSize: menu?.['font-size'],
    fontWeight: menu?.['font-weight']?.default,
    lineHeight: menu?.['line-height'],
    ellipsisOverflow: 'hidden',
    ellipsisTextOverflow: 'ellipsis',
  });
};
